import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { LayoutProps } from './authContext';
import { useSearchParams } from 'react-router-dom';
import { ICurrency, IRate } from '../interfaces/multiCurrencyInterface';
import { IBBPeriod, IInventoryCollateral, IInventoryInput, IInvIneligibleReport } from '../interfaces';
import { formatDate, getComparator, Order, stableSort } from '../utility/helper';
import usePermissions from '../utility/custom-hooks/usePermissions';
import { defaultUrlParameters, ISO_DATE_FORMAT, PARAMETER_DATE_FORMAT, PERMISSIONS } from '../utility/constants';
import { SelectedClientContext } from './selectedClientContext';
import useInvCollaterals from '../utility/custom-hooks/useInvCollaterals';
import useBbPeriods from '../utility/custom-hooks/useBbPeriods';
import useCurrencies from '../utility/custom-hooks/useCurrencies';
import useExchangeRates from '../utility/custom-hooks/useExchangeRates';
import useInvIneligibleReport from '../utility/custom-hooks/useInvIneligibleReport';
import { IItemWithIneligibles } from '../pages/reports/inv-ineligible/item-view';

export interface IInvIneligibleReportContext {
  canViewReport: boolean;
  canExportReport: boolean;
  collaterals: (IInventoryCollateral & IInventoryInput)[];
  setCollaterals: Dispatch<SetStateAction<(IInventoryCollateral & IInventoryInput)[]>>;
  bbPeriods: IBBPeriod[];
  setBbPeriods: Dispatch<SetStateAction<IBBPeriod[]>>;
  currencies: ICurrency[];
  setCurrencies: Dispatch<SetStateAction<ICurrency[]>>;
  selectedCollateral: (IInventoryCollateral & IInventoryInput) | null;
  setSelectedCollateral: Dispatch<SetStateAction<(IInventoryCollateral & IInventoryInput) | null>>;
  selectedBbPeriod: IBBPeriod | null;
  setSelectedBbPeriod: Dispatch<SetStateAction<IBBPeriod | null>>;
  selectedCurrency: ICurrency | null;
  setSelectedCurrency: Dispatch<SetStateAction<ICurrency | null>>;
  clientInput: string;
  setClientInput: Dispatch<SetStateAction<string>>;
  collateralInput: string;
  setCollateralInput: Dispatch<SetStateAction<string>>;
  bbPeriodInput: string;
  setBbPeriodInput: Dispatch<SetStateAction<string>>;
  currencyInput: string;
  setCurrencyInput: Dispatch<SetStateAction<string>>;
  invIneligibleReport: IInvIneligibleReport[];
  setInvIneligibleReport: Dispatch<SetStateAction<IInvIneligibleReport[]>>;
  allRates: IRate[];
  setAllRates: Dispatch<SetStateAction<IRate[]>>;
  exchangeRate: IRate | null;
  setExchangeRate: Dispatch<SetStateAction<IRate | null>>;
  rateWarningOpen: boolean;
  setRateWarningOpen: Dispatch<SetStateAction<boolean>>;
  ineligibleViewOrder: Order;
  setIneligibleViewOrder: Dispatch<SetStateAction<Order>>;
  ineligibleViewOrderBy: keyof IInvIneligibleReport;
  setIneligibleViewOrderBy: Dispatch<SetStateAction<keyof IInvIneligibleReport>>;
  itemViewOrder: Order;
  setItemViewOrder: Dispatch<SetStateAction<Order>>;
  itemViewOrderBy: keyof IItemWithIneligibles;
  setItemViewOrderBy: Dispatch<SetStateAction<keyof IItemWithIneligibles>>;
  triggerSort: boolean;
  setTriggerSort: Dispatch<SetStateAction<boolean>>;
  closeSearch: boolean;
  setCloseSearch: Dispatch<SetStateAction<boolean>>;
  isReportLoading: boolean;
  setIsReportLoading: Dispatch<SetStateAction<boolean>>;
  isGenerateReportDisabled: boolean;
  setIsGenerateReportDisabled: Dispatch<SetStateAction<boolean>>;
  isExporting: boolean;
  setIsExporting: Dispatch<SetStateAction<boolean>>;
  isExportingDisabled: boolean;
  // setIsExportingDisabled: Dispatch<SetStateAction<boolean>>;
  isSearched: boolean;
  setIsSearched: Dispatch<SetStateAction<boolean>>;
  displayNoTable: boolean;
  setDisplayNoTable: Dispatch<SetStateAction<boolean>>;
  viewBy: IInvIneligibleReportView;
  setViewBy: Dispatch<SetStateAction<IInvIneligibleReportView>>;
  currencyStyle: Intl.NumberFormatOptions;
  resetReport: () => void;
  resetRates: () => void;
  resetCurrencies: () => void;
  resetBbPeriods: () => void;
  resetCollaterals: () => void;
  updateSearchParams: (key: string, value: string) => void;
}

export type IInvIneligibleReportView = 'Ineligible' | 'Item';


export const InvIneligibleReportContext = createContext<IInvIneligibleReportContext | null>(null);

const InvIneligibleReportProvider = (props: LayoutProps) => {
  // -------------- Props --------------
  const { children } = props;
  
  // -------------- Parameters --------------
  const [searchParams, setSearchParams] = useSearchParams();
  const borrowerId = parseInt(searchParams.get('clientId') ?? '0');
  const bbPeriodId = parseInt(searchParams.get('bbPeriodId') ?? '0');
  const invCollateralId = parseInt(searchParams.get('invCollateralId') ?? '0');
  const currencyId = parseInt(searchParams.get('currencyId') ?? '0');
  
  // ----------- Hooks -----------
  const { checkPermission } = usePermissions();

  const { selectedClient } = useContext(SelectedClientContext);

  const {
    getInventoryCollateralOptions,
    selectCollateralByParameter
  } = useInvCollaterals();

  const {
    getBbPeriodOptions,
    selectBbPeriodByParameter
  } = useBbPeriods();

  const {
    getCurrencyOptions,
    selectCurrencyByParameter
  } = useCurrencies();

  const {
    getRates,
    checkCollateralRate
  } = useExchangeRates();

  const {
    getReportWithRates
  } = useInvIneligibleReport();

  // -------------- States --------------
  
  // Permissions
  const [canViewReport, setCanViewReport] = useState<boolean>(false);
  const [canExportReport, setCanExportReport] = useState<boolean>(false);

  // Dropdown Options
  const [collaterals, setCollaterals] = useState<(IInventoryCollateral & IInventoryInput)[]>([]);
  const [bbPeriods, setBbPeriods] = useState<IBBPeriod[]>([]);
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);

  // Dropdown Selections
  const [selectedCollateral, setSelectedCollateral] = useState<(IInventoryCollateral & IInventoryInput) | null>(null);
  const [selectedBbPeriod, setSelectedBbPeriod] = useState<IBBPeriod | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<ICurrency | null>(null);

  // Dropdown Inputs
  const [clientInput, setClientInput] = useState<string>('');
  const [collateralInput, setCollateralInput] = useState<string>('');
  const [bbPeriodInput, setBbPeriodInput] = useState<string>('');
  const [currencyInput, setCurrencyInput] = useState<string>('');

  // Inventory Report
  const [invIneligibleReport, setInvIneligibleReport] = useState<IInvIneligibleReport[]>([]);

  // Exchange Rate
  const [exchangeRate, setExchangeRate] = useState<IRate | null>(null);
  const [allRates, setAllRates] = useState<IRate[]>([]);
  const [rateWarningOpen, setRateWarningOpen] = useState<boolean>(false);

  // Sorting
  const [ineligibleViewOrder, setIneligibleViewOrder] = useState<Order>('asc');
  const [ineligibleViewOrderBy, setIneligibleViewOrderBy] = useState<keyof IInvIneligibleReport>('itemName');
  const [itemViewOrder, setItemViewOrder] = useState<Order>('asc');
  const [itemViewOrderBy, setItemViewOrderBy] = useState<keyof IItemWithIneligibles>('itemName');
  const [triggerSort, setTriggerSort] = useState<boolean>(false);
  const [closeSearch, setCloseSearch] = useState<boolean>(false);

  // Miscellaneous
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(true);
  const [isGenerateReportDisabled, setIsGenerateReportDisabled] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isExportingDisabled, setIsExportingDisabled] = useState<boolean>(true);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [displayNoTable, setDisplayNoTable] = useState<boolean>(false);
  const [viewBy, setViewBy] = useState<IInvIneligibleReportView>('Ineligible');

  // ----------- Effects -----------
  useEffect(() => {
    generateFirstReport();
    checkExportPermission();
  }, [])

  useEffect(() => {
    const filledDropdowns = selectedClient && selectedCollateral && selectedBbPeriod && selectedCurrency;
    
    if (!isFirstLoad && filledDropdowns && exchangeRate) {
      setIsGenerateReportDisabled(false);
    }
  }, [isFirstLoad, selectedClient, selectedCollateral, selectedBbPeriod, selectedCurrency, exchangeRate])

  useEffect(() => {
    const generatedReportNotEmpty = Boolean(invIneligibleReport.length);
    setIsExportingDisabled(!(canExportReport && generatedReportNotEmpty));
  }, [invIneligibleReport, canExportReport])

  // ----------- Functions -----------
  const generateFirstReport = async () => {
    setIsReportLoading(true);

    const canViewReport = await checkPermission(PERMISSIONS.VIEW_INVENTORY_REPORT);
    setCanViewReport(canViewReport);

    if (canViewReport && borrowerId) {
      // Collaterals
      const collaterals = await getInventoryCollateralOptions(borrowerId);
      setCollaterals(collaterals);

      const selectedCollateral = selectCollateralByParameter(invCollateralId, collaterals);
      setSelectedCollateral(selectedCollateral);

      // Bb Periods
      const availableBbPeriods = await getBbPeriodOptions(borrowerId, invCollateralId);
      setBbPeriods(availableBbPeriods);

      const selectedBbPeriod = selectBbPeriodByParameter(bbPeriodId, availableBbPeriods);
      setSelectedBbPeriod(selectedBbPeriod);
      
      // Currencies
      const collateralCurrencyIds = [parseInt(selectedCollateral?.currency ?? '0')]
      const borrowerCurrencyId = parseInt(selectedClient?.currencyId ?? '150');

      const currencies = await getCurrencyOptions([borrowerCurrencyId, ...collateralCurrencyIds]);
      setCurrencies(currencies);

      const selectedCurrency = selectCurrencyByParameter(currencyId, currencies);
      setSelectedCurrency(selectedCurrency);

      // Rates
      const allRates = await getRates(
        borrowerId,
        borrowerCurrencyId,
        formatDate(selectedBbPeriod?.endDate ?? '', ISO_DATE_FORMAT),
        collateralCurrencyIds,
        parseInt(selectedCollateral?.currency ?? '0')
      );
      setAllRates(allRates);

      const exchangeRate = checkCollateralRate(
        parseInt(selectedCollateral?.currency ?? '0'),
        selectedCurrency?.recordId ?? 0,
        allRates
      )
      setExchangeRate(exchangeRate);

      if (exchangeRate) {
        const report = await getReportWithRates(borrowerId, bbPeriodId, invCollateralId, exchangeRate.currencyRate);
        
        if (report.length) {
          setInvIneligibleReport(stableSort(report, getComparator('asc', 'calcOrder', 'others')));
        } else {
          setDisplayNoTable(true);
        }
      } else {
        setRateWarningOpen(true);
      }
    }

    setIsReportLoading(false);
  }

  const checkExportPermission = async () => {
    const canExportReport = await checkPermission(PERMISSIONS.EXPORT_INELIGIBLE_REPORT);
    setCanExportReport(canExportReport);
  }

  const resetReport = () => {
    setIneligibleViewOrder('asc');
    setIneligibleViewOrderBy('itemName');
    setItemViewOrder('asc');
    setItemViewOrderBy('itemName');
    setIsGenerateReportDisabled(true);
    setIsExportingDisabled(true);
    setIsSearched(false);
    setDisplayNoTable(false);
    setInvIneligibleReport([]);
    setIsFirstLoad(false);
  }

  const resetRates = () => {
    setAllRates([]);
    setExchangeRate(null);
  }

  const resetCurrencies = () => {
    updateSearchParams(defaultUrlParameters.CURRENCY_ID, '0');
    setSelectedCurrency(null);
    setCurrencies([]);
  }

  const resetBbPeriods = () => {
    updateSearchParams(defaultUrlParameters.BB_PERIOD_ID, '0');
    setSelectedBbPeriod(null);
    setBbPeriods([]);
  }

  const resetCollaterals = () => {
    updateSearchParams(defaultUrlParameters.INV_COLLATERAL_ID, '0');
    setSelectedCollateral(null);
    setCollaterals([]);
  }

  const updateSearchParams = (key: string, value: string) => {
    searchParams.set(key, value);
    setSearchParams(searchParams);
  };

  const currencyStyle = useMemo(() => {
    const formatProps: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: selectedCurrency?.currencyCode ?? 'USD',
      currencySign: 'accounting'
    }

    return formatProps;
  }, [selectedCurrency])

  const value = useMemo(() => ({
    canViewReport,
    canExportReport,
    clientInput, setClientInput,
    isReportLoading, setIsReportLoading,
    isExporting, setIsExporting,
    isExportingDisabled,
    isGenerateReportDisabled, setIsGenerateReportDisabled,
    isSearched, setIsSearched,
    rateWarningOpen, setRateWarningOpen,
    viewBy, setViewBy,
    displayNoTable, setDisplayNoTable,
    collaterals, setCollaterals,
    collateralInput, setCollateralInput,
    selectedCollateral, setSelectedCollateral,
    bbPeriods, setBbPeriods,
    bbPeriodInput, setBbPeriodInput,
    selectedBbPeriod, setSelectedBbPeriod,
    currencies, setCurrencies,
    currencyInput, setCurrencyInput,
    selectedCurrency, setSelectedCurrency,
    invIneligibleReport, setInvIneligibleReport,
    allRates, setAllRates,
    exchangeRate, setExchangeRate,
    currencyStyle,
    ineligibleViewOrder, setIneligibleViewOrder,
    ineligibleViewOrderBy, setIneligibleViewOrderBy,
    itemViewOrder, setItemViewOrder,
    itemViewOrderBy, setItemViewOrderBy,
    triggerSort, setTriggerSort,
    closeSearch, setCloseSearch,
    resetReport, resetRates,
    resetCurrencies, resetBbPeriods, resetCollaterals,
    updateSearchParams,
  }), [
    canViewReport,
    canExportReport,
    clientInput,
    isReportLoading,
    isExporting,
    isExportingDisabled,
    isGenerateReportDisabled,
    isSearched,
    rateWarningOpen,
    viewBy,
    displayNoTable,
    collaterals,
    collateralInput,
    selectedCollateral,
    bbPeriods,
    bbPeriodInput,
    selectedBbPeriod,
    currencies,
    currencyInput,
    selectedCurrency,
    invIneligibleReport,
    allRates,
    exchangeRate,
    currencyStyle,
    ineligibleViewOrder,
    ineligibleViewOrderBy,
    triggerSort,
    closeSearch
  ]);
  
  return (
    <InvIneligibleReportContext.Provider value={value}>
      {children}
    </InvIneligibleReportContext.Provider>
  );
};

export default InvIneligibleReportProvider;
